import {
  Badge,
  DateCell,
  DocumentCheckIcon,
  NameCell,
  NotScheduledCell,
  PaperClipIcon,
  PhotoIcon,
  PlayIcon,
  SubduedCell,
  useEffect,
  useRef,
  useState,
} from "@";
import type { ColumnDef } from "@tanstack/react-table";
import type { VerificationStage } from "../types.ts";
import {
  VerificationStageStatus,
  VerificationStageType,
} from "@/features/operations/verifications/types.ts";
import { Link } from "react-router-dom";

interface TableActionsProps {
  row: VerificationStage;
  onAttachmentClick: (
    verificationStageId: number,
    attachmentIndex: number,
  ) => void;
}

const MAX_VISIBLE_ATTACHMENTS = 5;

const LazyThumb = ({ src, alt = "" }: { src: string; alt?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      // Fallback: just load it
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  return (
    <div ref={ref} className="relative h-full w-full overflow-hidden">
      {/* Shimmer skeleton while the image is loading */}
      {!loaded && !errored && (
        <span
          aria-hidden
          className="absolute inset-0 animate-pulse bg-gradient-to-br from-gray-100 via-gray-200 to-gray-100 dark:from-gray-700 dark:via-gray-800 dark:to-gray-700"
        />
      )}

      {/* Error fallback */}
      {errored && (
        <span className="flex h-full w-full items-center justify-center bg-gray-50 text-gray-400 dark:bg-gray-700 dark:text-gray-500">
          <PhotoIcon className="h-5 w-5" />
        </span>
      )}

      {/* Image — only mounted once near the viewport */}
      {inView && !errored && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          // @ts-expect-error fetchpriority is a valid HTML attribute (Chrome/Edge/Safari) not yet in React's typings
          fetchpriority="low"
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={`h-full w-full object-cover transition-opacity duration-300 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </div>
  );
};

// Same lazy-mount strategy as LazyThumb, but for video: `preload="metadata"`
// + a `#t=` media fragment makes the browser range-fetch just the header and
// one frame, then paint it as a still — no full download, no playback loop.
const LazyVideoThumb = ({ src }: { src: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  return (
    <div
      ref={ref}
      className="relative h-full w-full overflow-hidden rounded bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900"
    >
      {/* Dot texture / shimmer until the poster frame paints */}
      {(!loaded || errored) && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-15 [background-image:radial-gradient(circle_at_1px_1px,_white_1px,_transparent_0)] [background-size:10px_10px]"
        />
      )}

      {inView && !errored && (
        <video
          // Seek 0.5s in so we skip the usual black opening frame
          src={`${src}#t=0.5`}
          preload="metadata"
          muted
          playsInline
          // @ts-expect-error fetchpriority is a valid HTML attribute not yet in React's typings
          fetchpriority="low"
          onLoadedData={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={`h-full w-full rounded object-cover transition-opacity duration-300 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
      )}

      <span
        aria-hidden
        className="absolute bottom-2 end-2 inline-flex h-4 w-4 rounded-full bg-rose-500 ring-2 ring-white"
      />
    </div>
  );
};

export const TableActions = ({ row, onAttachmentClick }: TableActionsProps) => {
  // videos first to match prior UX
  const attachments = [...(row?.attachments ?? [])].sort(
    (a, b) => b.type - a.type,
  );
  const stageId = row.verificationStageId;
  const total = attachments.length;

  const cellWidthClass = "flex items-center";

  // Empty state — same width container so the column stays stable
  if (!total || stageId === undefined) {
    return (
      <div className={cellWidthClass}>
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-dashed border-gray-200 px-2.5 py-1 text-[11px] font-medium text-gray-400 dark:border-gray-700 dark:text-gray-500">
          <PaperClipIcon className="h-3.5 w-3.5" />
          لا يوجد مرفقات
        </span>
      </div>
    );
  }

  const visible = attachments.slice(0, MAX_VISIBLE_ATTACHMENTS);
  const hidden = total - visible.length;

  return (
    <div className={`${cellWidthClass} gap-4`}>
      {visible.map((file, index) => {
        const isImage = file.type === 1;
        return (
          <button
            key={index}
            type="button"
            onClick={() => onAttachmentClick(stageId, index)}
            aria-label={isImage ? `صورة ${index + 1}` : `فيديو ${index + 1}`}
            title={isImage ? `صورة ${index + 1}` : `فيديو ${index + 1}`}
            className="group/att relative inline-flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded bg-white transition-all duration-200 first:ms-0 hover:z-10 hover:scale-110 hover:ring-purple-300 dark:bg-gray-800 dark:ring-gray-800 dark:hover:ring-purple-700"
            style={{ zIndex: 0 }}
          >
            {isImage ? (
              <LazyThumb src={file.url} alt={`صورة ${index + 1}`} />
            ) : (
              <LazyVideoThumb src={file.url} />
            )}
            {/* Hover gradient overlay */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 transition-opacity duration-200 group-hover/att:opacity-100"
            />
          </button>
        );
      })}

      {hidden > 0 && (
        <button
          type="button"
          onClick={() => onAttachmentClick(stageId, MAX_VISIBLE_ATTACHMENTS)}
          aria-label={`عرض ${hidden} مرفقات إضافية`}
          title={`عرض ${hidden} مرفقات إضافية`}
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100 text-[12px] font-bold tabular-nums tracking-tight text-purple-900 shadow-sm shadow-purple-900/10 ring-2 ring-white transition-all duration-200 hover:z-10 hover:scale-110 hover:from-purple-100 hover:to-purple-200 hover:ring-purple-300 dark:from-purple-950/60 dark:to-purple-900/40 dark:text-purple-300 dark:ring-gray-800 dark:hover:from-purple-900/60 dark:hover:to-purple-800/40 dark:hover:ring-purple-700"
          style={{ zIndex: 0 }}
        >
          +{hidden}
        </button>
      )}
    </div>
  );
};

// Order ID — keep the link semantics (navigates to the donation), but
// upgrade to a brand-purple chip instead of the generic blue underline.
const OrderIdLink = ({ orderId }: { orderId: number }) => (
  <Link
    to={`/donations?toDate=&fromDate=&id=${orderId}`}
    className="inline-flex items-center rounded-md bg-purple-50 px-2 py-0.5 font-mono text-[12px] font-semibold tabular-nums text-purple-900 ring-1 ring-purple-100 transition-colors hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40 dark:hover:bg-purple-900/40"
  >
    {orderId}
  </Link>
);

// Project + attribute — primary name with the secondary attribute below it.
const ProjectAttributeCell = ({
  projectName,
  attributeName,
}: {
  projectName?: string;
  attributeName?: string;
}) => (
  <div className="flex flex-col leading-tight">
    <NameCell name={projectName} />
    {attributeName && <SubduedCell value={attributeName} />}
  </div>
);

// Stage column — number-prefixed name + first-stage-verified badge below.
const StageCell = ({
  stageNumber,
  stageName,
  isFirstStageVerified,
}: {
  stageNumber?: number;
  stageName?: string;
  isFirstStageVerified?: boolean;
}) => (
  <div className="flex flex-col gap-1.5">
    <span className="inline-flex items-baseline gap-1 font-medium tracking-tight text-gray-900 dark:text-gray-100">
      <span className="tabular-nums text-gray-500 dark:text-gray-500">
        {stageNumber}
      </span>
      <span className="text-gray-400 dark:text-gray-600">·</span>
      <span>{stageName}</span>
    </span>
    {isFirstStageVerified && (
      <span>
        <Badge title="تم توثيق المرحلة الأولى" color="green" />
      </span>
    )}
  </div>
);

const getStatusBadgeProps = (status: VerificationStageStatus) => {
  const statusMap: Record<
    VerificationStageStatus,
    { title: string; color: string }
  > = {
    0: { title: "لم يتم البدء", color: "default" },
    1: { title: "تم الاسناد", color: "default" },
    2: { title: " انتظار التحقق", color: "yellow" },
    3: { title: "تم التحقق", color: "green" },
    4: { title: "مرفوض", color: "red" },
    5: { title: " انتظار وقت الإرسال", color: "yellow" },
    6: { title: "جاري معالجة الملفات", color: "blue" },
  };
  return statusMap[status] || { title: "", color: "" };
};

// TanStack table columns format
export const tableColumns: ColumnDef<VerificationStage>[] = [
  {
    id: "orderId",
    size: 95,
    header: "رقم الطلب",
    cell: ({ row }) => <OrderIdLink orderId={row.original.orderId} />,
  },
  {
    size: 140,
    id: "customerName",
    header: "اسم العميل",
    cell: ({ row }) => <NameCell name={row.original.customerName} />,
  },
  {
    size: 130,
    id: "verificationStageStatus",
    header: "الحالة",
    cell: ({ row }) => {
      const { title, color } = getStatusBadgeProps(
        row.original.verificationStageStatus as VerificationStageStatus,
      );
      const isManual = row.original.type === VerificationStageType.Manual;
      return (
        <div className="flex flex-col gap-1.5">
          <Badge title={title} color={color} />
          {isManual && <Badge title="توثيق يدوي" color="dark" />}
        </div>
      );
    },
  },
  {
    size: 200,
    id: "projectName",
    header: "المشروع / الفئة",
    cell: ({ row }) => (
      <ProjectAttributeCell
        projectName={row.original.projectName}
        attributeName={row.original.attributeName}
      />
    ),
  },
  {
    size: 130,
    id: "projectClassification",
    header: "تصنيف الطلب",
    cell: ({ row }) => (
      <SubduedCell value={row.original.projectClassification} />
    ),
  },
  {
    size: 140,
    id: "stageName",
    header: "المرحلة",
    cell: ({ row }) => (
      <StageCell
        stageNumber={row.original.stageNumber}
        stageName={row.original.stageName}
        isFirstStageVerified={row.original.isFirstStageVerified}
      />
    ),
  },
  {
    size: 120,
    id: "smsSendingDate",
    header: "تاريخ التنفيذ",
    cell: ({ row }) => {
      const date = row.original.smsSendingDate;
      if (!date) {
        return (
          <NotScheduledCell
            id={`verify-${row.original.verificationStageId ?? row.original.orderId}`}
          />
        );
      }
      return <DateCell value={date} />;
    },
  },
  {
    id: "orderCreationDate",
    size: 120,
    header: "تاريخ الإنشاء",
    cell: ({ row }) => <DateCell value={row.original.orderCreationDate} />,
  },
  {
    size: 80,
    id: "isPrinted",
    header: "مطبوع",
    cell: ({ row }) => {
      const isPrinted = row.original.isPrinted;
      if (isPrinted === undefined) {
        return (
          <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
        );
      }
      return (
        <Badge
          title={isPrinted ? "نعم" : "لا"}
          color={isPrinted ? "green" : "red"}
        >
          <DocumentCheckIcon className="inline-block h-4 w-4" />
        </Badge>
      );
    },
  },
  {
    size: 120,
    id: "driverName",
    header: "المندوب",
    cell: ({ row }) => <NameCell name={row.original.driverName} />,
  },
  {
    size: 170,
    id: "iftarProjectBeneficiariesNames",
    header: "ملاحظات",
    cell: ({ row }) => {
      const names = row.original.iftarProjectBeneficiariesNames;
      const beneficiary = row.original.beneficiaryName;
      if ((!names || names.length === 0) && !beneficiary) {
        return (
          <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
        );
      }
      return (
        <ul className="flex flex-col gap-1.5">
          {names?.map((name) => (
            <li
              key={name}
              title={name}
              className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-300"
            >
              {name.length > 40 ? name.slice(0, 40) + "…" : name}
            </li>
          ))}
          {beneficiary && (
            <li className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-300">
              {beneficiary}
            </li>
          )}
        </ul>
      );
    },
  },
];

export default tableColumns;
